import React, { useState, useEffect } from "react";
import {
  verifyPaymentCredentials,
  getBusinessPaymentMethods,
  removePaymentMethod,
} from "../services/payment.service";
import { useTranslation } from "react-i18next";
import useTheme from "../constants/there";

const PaymentGatewayPopup = ({
  businessUuid,
  businessName,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { themeMode } = useTheme();
  const isDark = themeMode === "dark";

  const [selectedGateway, setSelectedGateway] = useState("");
  const [credentials, setCredentials] = useState({
    razorpay: { keyId: "", keySecret: "", merchantId: "" },
    cashfree: { appId: "", secretKey: "", isLive: false },
  });
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [paymentMethods, setPaymentMethods] = useState({
    razorpay: { configured: false, isVerified: false },
    cashfree: { configured: false, isVerified: false },
    enabledGateway: "none",
  });
  const [showRemoveConfirm, setShowRemoveConfirm] = useState({
    show: false,
    gateway: null,
  });

  useEffect(() => {
    if (isOpen && businessUuid) {
      loadPaymentMethods();
    }
  }, [isOpen, businessUuid]);

  const loadPaymentMethods = async () => {
    try {
      const response = await getBusinessPaymentMethods(businessUuid);
      if (response.success) {
        setPaymentMethods(response.data);
      }
    } catch (error) {
      console.error("Error loading payment methods:", error);
    }
  };

  const handleCredentialChange = (gateway, field, value) => {
    setCredentials((prev) => ({
      ...prev,
      [gateway]: {
        ...prev[gateway],
        [field]: value,
      },
    }));
  };

  const handleRemoveClick = (gateway) => {
    setShowRemoveConfirm({
      show: true,
      gateway: gateway,
    });
  };

  const handleRemoveConfirm = async () => {
    const { gateway } = showRemoveConfirm;

    try {
      const result = await removePaymentMethod(businessUuid, gateway);
      if (result.success) {
        setSuccess(t("PAYMENT.REMOVAL_SUCCESS", { gateway }));
        await loadPaymentMethods();
        onSuccess && onSuccess();
      }
    } catch (error) {
      setError(t("PAYMENT.REMOVAL_FAILED", { gateway, error: error.message }));
    } finally {
      setShowRemoveConfirm({ show: false, gateway: null });
    }
  };

  const handleRemoveCancel = () => {
    setShowRemoveConfirm({ show: false, gateway: null });
  };

  const handleVerify = async () => {
    if (!selectedGateway) {
      setError(t("PAYMENT.ERROR_SELECT_GATEWAY"));
      return;
    }

    const gatewayCredentials = credentials[selectedGateway];

    // Basic validation
    if (
      selectedGateway === "razorpay" &&
      (!gatewayCredentials.keyId || !gatewayCredentials.keySecret)
    ) {
      setError(t("PAYMENT.ERROR_RAZORPAY_CREDENTIALS"));
      return;
    }

    if (
      selectedGateway === "cashfree" &&
      (!gatewayCredentials.appId || !gatewayCredentials.secretKey)
    ) {
      setError(t("PAYMENT.ERROR_CASHFREE_CREDENTIALS"));
      return;
    }

    setIsVerifying(true);
    setError("");
    setSuccess("");

    try {
      const result = await verifyPaymentCredentials(
        selectedGateway,
        gatewayCredentials,
        businessUuid,
        businessName
      );

      if (result.success) {
        setSuccess(
          t("PAYMENT.VERIFICATION_SUCCESS", {
            message: result.message,
            status: result.isVerified
              ? t("PAYMENT.VERIFIED")
              : t("PAYMENT.NOT_VERIFIED"),
          })
        );
        setCredentials({
          razorpay: { keyId: "", keySecret: "", merchantId: "" },
          cashfree: { appId: "", secretKey: "", isLive: false },
        });
        setSelectedGateway("");
        await loadPaymentMethods();
        onSuccess && onSuccess();

        if (result.isVerified) {
          setTimeout(() => {
            onClose();
          }, 3000);
        }
      } else {
        setError(
          t("PAYMENT.VERIFICATION_FAILED", { message: result.message })
        );
      }
    } catch (error) {
      setError(`${t("PAYMENT.NOT_VERIFIED")}: ${error.message}`);
    } finally {
      setIsVerifying(false);
    }
  };

  // Check if no payment methods are configured
  const noGatewaysConfigured =
    !paymentMethods.razorpay.configured && !paymentMethods.cashfree.configured;

  if (!isOpen) return null;

  return (
    <>
      {/* Custom Remove Confirmation Popup */}
      {showRemoveConfirm.show && (
        <div className='fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4'>
          <div className={`${isDark ? "bg-slate-900 border border-slate-700" : "bg-white"} w-full max-w-md rounded-2xl shadow-2xl transform transition-all animate-fade-in`}>
            {/* Header */}
            <div className={`p-6 border-b ${isDark ? "border-slate-800" : "border-gray-200"}`}>
              <div className='flex items-center space-x-3'>
                <div className='w-10 h-10 bg-red-100 rounded-full flex items-center justify-center'>
                  <svg
                    className='w-5 h-5 text-red-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z'
                    />
                  </svg>
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
                    {t("PAYMENT.REMOVE_CONFIRMATION_TITLE")}
                  </h3>
                  <p className={`${isDark ? "text-slate-400" : "text-gray-600"} text-sm mt-1`}>
                    {t("PAYMENT.REMOVE_CONFIRMATION_DESCRIPTION", { gateway: showRemoveConfirm.gateway })}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className='p-6'>
              <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4'>
                  <div className='flex items-start space-x-3'>
                    {/* SVG icon remains same */}
                    <svg
                      className='w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                      />
                    </svg>
                    <div>
                      <p className='text-yellow-800 text-sm font-medium'>
                        {t("PAYMENT.REMOVE_WARNING_TITLE")}
                      </p>
                      <p className='text-yellow-700 text-sm mt-1'>
                        {t("PAYMENT.REMOVE_WARNING_DESCRIPTION", { gateway: showRemoveConfirm.gateway })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
  
              {/* Actions */}
              <div className={`flex justify-end space-x-3 p-6 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
                <button
                  onClick={handleRemoveCancel}
                  className={`px-6 py-2.5 border ${isDark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"} rounded-lg transition-colors duration-200 font-medium`}
                >
                  {t("COMMON.CANCEL")}
                </button>
                <button
                  onClick={handleRemoveConfirm}
                  className='px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium flex items-center space-x-2'
                >
                  {/* SVG icon remains same */}
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                    />
                  </svg>
                  <span>{t("PAYMENT.REMOVE")}</span>
                </button>
              </div>
          </div>
        </div>
      )}

      {/* Main Payment Gateway Popup */}
      <div className='fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4'>
        <div className={`${isDark ? "bg-slate-900 border border-slate-700" : "bg-white"} w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden transform transition-all`}>
          {/* Header */}
          <div className={`${isDark ? "bg-slate-800" : "bg-blue-600"} p-4 text-white`}>
            <div className='flex justify-between items-center'>
              <div>
                <h2 className='text-2xl font-bold'>
                  {t("PAYMENT.SETUP_TITLE")}
                </h2>
                <p className={`${isDark ? "text-slate-400" : "text-blue-100"} text-sm mt-1`}>
                  {t("PAYMENT.SETUP_DESCRIPTION")}
                </p>
              </div>
              <button
                onClick={onClose}
                className='text-white hover:bg-white/20 p-2 rounded-full transition-colors duration-200'
              >
                <svg
                  className='w-6 h-6'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className='p-6 space-y-6 max-h-[70vh] overflow-y-auto'>
            {/* Status Cards */}
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                <div
                className={`bg-gradient-to-br p-4 rounded-xl border-l-4 ${
                  paymentMethods.razorpay.isVerified
                    ? (isDark ? "from-green-900/20 to-green-800/20 border-green-500" : "from-green-50 to-green-100 border-green-500")
                    : paymentMethods.razorpay.configured
                    ? (isDark ? "from-yellow-900/20 to-yellow-800/20 border-yellow-500" : "from-yellow-50 to-yellow-100 border-yellow-500")
                    : (isDark ? "from-slate-800 to-slate-800 border-slate-700" : "from-gray-50 to-gray-100 border-gray-300")
                }`}
              >
                <div className='flex items-center justify-between'>
                  <span className={`font-semibold ${isDark ? "text-slate-200" : "text-gray-700"}`}>{t("PAYMENT.RAZORPAY")}</span>
                  <div
                    className={`w-3 h-3 rounded-full ${
                      paymentMethods.razorpay.isVerified
                        ? "bg-green-500"
                        : paymentMethods.razorpay.configured
                        ? "bg-yellow-500"
                        : "bg-gray-300"
                    }`}
                  />
                </div>
                <p className='text-sm text-gray-600 mt-2'>
                  {paymentMethods.razorpay.configured
                    ? paymentMethods.razorpay.isVerified
                      ? t("PAYMENT.VERIFIED_ACTIVE")
                      : t("PAYMENT.CONFIGURED_NEEDS_VERIFICATION")
                    : t("PAYMENT.NOT_CONFIGURED")}
                </p>
              </div>

              <div
                className={`bg-gradient-to-br p-4 rounded-xl border-l-4 ${
                  paymentMethods.cashfree.isVerified
                    ? (isDark ? "from-green-900/20 to-green-800/20 border-green-500" : "from-green-50 to-green-100 border-green-500")
                    : paymentMethods.cashfree.configured
                    ? (isDark ? "from-yellow-900/20 to-yellow-800/20 border-yellow-500" : "from-yellow-50 to-yellow-100 border-yellow-500")
                    : (isDark ? "from-slate-800 to-slate-800 border-slate-700" : "from-gray-50 to-gray-100 border-gray-300")
                }`}
              >
                <div className='flex items-center justify-between'>
                  <span className={`font-semibold ${isDark ? "text-slate-200" : "text-gray-700"}`}>{t("PAYMENT.CASHFREE")}</span>
                  <div
                    className={`w-3 h-3 rounded-full ${
                      paymentMethods.cashfree.isVerified
                        ? "bg-green-500"
                        : paymentMethods.cashfree.configured
                        ? "bg-yellow-500"
                        : "bg-gray-300"
                    }`}
                  />
                </div>
                <p className='text-sm text-gray-600 mt-2'>
                  {paymentMethods.cashfree.configured
                    ? paymentMethods.cashfree.isVerified
                      ? t("PAYMENT.VERIFIED_ACTIVE")
                      : t("PAYMENT.CONFIGURED_NEEDS_VERIFICATION")
                    : t("PAYMENT.NOT_CONFIGURED")}
                </p>
              </div>

              <div className={`bg-gradient-to-br ${isDark ? "from-blue-900/20 to-purple-900/20 border-blue-500" : "from-blue-50 to-purple-50 border-blue-500"} p-4 rounded-xl border-l-4`}>
                <div className='flex items-center justify-between'>
                  <span className={`font-semibold ${isDark ? "text-slate-200" : "text-gray-700"}`}>
                    {t("PAYMENT.ACTIVE_GATEWAY")}
                  </span>
                  <div className='w-3 h-3 bg-purple-500 rounded-full' />
                </div>
                <p className={`text-sm font-medium ${isDark ? "text-purple-400" : "text-purple-600"} mt-2 capitalize`}>
                  {paymentMethods.enabledGateway}
                </p>
              </div>
            </div>

            {/* Alerts */}
            {error && (
              <div className={`${isDark ? "bg-red-900/20 border-red-800 text-red-400" : "bg-red-50 border-red-200 text-red-700"} border rounded-xl p-4 flex items-start space-x-3 animate-shake`}>
                <div className='flex-shrink-0 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center'>
                  <svg
                    className='w-4 h-4 text-red-600'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <p className='flex-1'>{error}</p>
              </div>
            )}

            {success && (
              <div className='bg-green-50 border border-green-200 rounded-xl p-4 flex items-start space-x-3 animate-fade-in'>
                <div className='flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center'>
                  <svg
                    className='w-4 h-4 text-green-600'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <p className='text-green-700 flex-1'>{success}</p>
              </div>
            )}
{/* Gateway Selection */}
<div className='space-y-4'>
  <h3 className={`font-semibold text-lg ${isDark ? "text-slate-200" : "text-gray-800"} flex items-center`}>
    <svg
      className='w-5 h-5 text-gray-500 mr-2'
      fill='none'
      stroke='currentColor'
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z'
      />
    </svg>
    {t("PAYMENT.SELECT_GATEWAY")}
  </h3>

  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
    {/* Razorpay Card */}
    <div
      className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105 ${
        selectedGateway === "razorpay"
          ? isDark 
            ? "border-blue-500 bg-blue-900/30 shadow-lg shadow-blue-500/20" 
            : "border-blue-500 bg-blue-50 shadow-lg"
          : paymentMethods.razorpay.isVerified
          ? (isDark ? "border-green-800 bg-slate-800/50 hover:border-green-700" : "border-green-300 bg-white hover:border-green-400")
          : paymentMethods.razorpay.configured
          ? (isDark ? "border-yellow-800 bg-slate-800/50 hover:border-yellow-700" : "border-yellow-300 bg-white hover:border-yellow-400")
          : (isDark ? "border-slate-700 bg-slate-800/50 hover:border-slate-600" : "border-gray-200 bg-white hover:border-gray-300")
      }`}
      onClick={() => setSelectedGateway("razorpay")}
    >
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center space-x-3'>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            selectedGateway === "razorpay"
              ? isDark ? "bg-blue-900/50" : "bg-blue-100"
              : "bg-blue-100"
          }`}>
            <span className={`font-bold text-sm ${
              selectedGateway === "razorpay"
                ? isDark ? "text-blue-400" : "text-blue-600"
                : "text-blue-600"
            }`}>
              RP
            </span>
          </div>
          <span className={`font-semibold ${isDark ? "text-slate-200" : "text-gray-800"}`}>
            {t("PAYMENT.RAZORPAY")}
          </span>
        </div>
        <input
          type='radio'
          name='gateway'
          value='razorpay'
          checked={selectedGateway === "razorpay"}
          onChange={(e) => setSelectedGateway(e.target.value)}
          className={`h-5 w-5 text-blue-600 focus:ring-blue-500 ${
            isDark ? "bg-slate-800 border-slate-600" : ""
          }`}
        />
      </div>

      {paymentMethods.razorpay.configured && (
        <div className='flex items-center justify-between mt-2'>
          <span
            className={`text-sm font-medium px-2 py-1 rounded-full ${
              paymentMethods.razorpay.isVerified
                ? isDark ? "bg-green-900/50 text-green-400" : "bg-green-100 text-green-700"
                : isDark ? "bg-yellow-900/50 text-yellow-400" : "bg-yellow-100 text-yellow-700"
            }`}
          >
            {paymentMethods.razorpay.isVerified
              ? t("PAYMENT.VERIFIED")
              : t("PAYMENT.NOT_VERIFIED")}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveClick("razorpay");
            }}
            className={`text-sm font-medium flex items-center space-x-1 ${
              isDark ? "text-red-400 hover:text-red-300" : "text-red-500 hover:text-red-700"
            }`}
          >
            <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
              />
            </svg>
            <span>{t("PAYMENT.REMOVE")}</span>
          </button>
        </div>
      )}
    </div>

    {/* Cashfree Card */}
    <div
      className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105 ${
        selectedGateway === "cashfree"
          ? isDark 
            ? "border-blue-500 bg-blue-900/30 shadow-lg shadow-blue-500/20" 
            : "border-blue-500 bg-blue-50 shadow-lg"
          : paymentMethods.cashfree.isVerified
          ? (isDark ? "border-green-800 bg-slate-800/50 hover:border-green-700" : "border-green-300 bg-white hover:border-green-400")
          : paymentMethods.cashfree.configured
          ? (isDark ? "border-yellow-800 bg-slate-800/50 hover:border-yellow-700" : "border-yellow-300 bg-white hover:border-yellow-400")
          : (isDark ? "border-slate-700 bg-slate-800/50 hover:border-slate-600" : "border-gray-200 bg-white hover:border-gray-300")
      }`}
      onClick={() => setSelectedGateway("cashfree")}
    >
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center space-x-3'>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            selectedGateway === "cashfree"
              ? isDark ? "bg-orange-900/50" : "bg-orange-100"
              : "bg-orange-100"
          }`}>
            <span className={`font-bold text-sm ${
              selectedGateway === "cashfree"
                ? isDark ? "text-orange-400" : "text-orange-600"
                : "text-orange-600"
            }`}>
              CF
            </span>
          </div>
          <span className={`font-semibold ${isDark ? "text-slate-200" : "text-gray-800"}`}>
            {t("PAYMENT.CASHFREE")}
          </span>
        </div>
        <input
          type='radio'
          name='gateway'
          value='cashfree'
          checked={selectedGateway === "cashfree"}
          onChange={(e) => setSelectedGateway(e.target.value)}
          className={`h-5 w-5 text-blue-600 focus:ring-blue-500 ${
            isDark ? "bg-slate-800 border-slate-600" : ""
          }`}
        />
      </div>

      {paymentMethods.cashfree.configured && (
        <div className='flex items-center justify-between mt-2'>
          <span
            className={`text-sm font-medium px-2 py-1 rounded-full ${
              paymentMethods.cashfree.isVerified
                ? isDark ? "bg-green-900/50 text-green-400" : "bg-green-100 text-green-700"
                : isDark ? "bg-yellow-900/50 text-yellow-400" : "bg-yellow-100 text-yellow-700"
            }`}
          >
            {paymentMethods.cashfree.isVerified
              ? t("PAYMENT.VERIFIED")
              : t("PAYMENT.NOT_VERIFIED")}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveClick("cashfree");
            }}
            className={`text-sm font-medium flex items-center space-x-1 ${
              isDark ? "text-red-400 hover:text-red-300" : "text-red-500 hover:text-red-700"
            }`}
          >
            <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
              />
            </svg>
            <span>{t("PAYMENT.REMOVE")}</span>
          </button>
        </div>
      )}
    </div>
  </div>
</div>

           {selectedGateway === "razorpay" && (
  <>
    <div className='space-y-3'>
      <div>
        <label className={`block text-sm font-medium ${isDark ? "text-slate-300" : "text-gray-700"} mb-1`}>
          {t("PAYMENT.KEY_ID")}
        </label>
        <input
          type='text'
          value={credentials.razorpay.keyId}
          onChange={(e) =>
            handleCredentialChange(
              "razorpay",
              "keyId",
              e.target.value
            )
          }
          placeholder={t("PAYMENT.KEY_ID_PLACEHOLDER") || 'rzp_test_xxxxxxxxxxxx'}
          className={`w-full px-4 py-3 border ${isDark ? "bg-slate-900 border-slate-700 text-white placeholder-slate-600 focus:ring-blue-500" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"} rounded-lg focus:ring-2 transition-colors duration-200`}
        />
      </div>
      <div>
        <label className={`block text-sm font-medium ${isDark ? "text-slate-300" : "text-gray-700"} mb-1`}>
          {t("PAYMENT.KEY_SECRET")}
        </label>
        <input
          type='password'
          value={credentials.razorpay.keySecret}
          onChange={(e) =>
            handleCredentialChange(
              "razorpay",
              "keySecret",
              e.target.value
            )
          }
          placeholder={t("PAYMENT.KEY_SECRET_PLACEHOLDER")}
          className={`w-full px-4 py-3 border ${isDark ? "bg-slate-900 border-slate-700 text-white placeholder-slate-600 focus:ring-blue-500" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"} rounded-lg focus:ring-2 transition-colors duration-200`}
        />
      </div>
      <div>
        <label className={`block text-sm font-medium ${isDark ? "text-slate-300" : "text-gray-700"} mb-1`}>
          {t("PAYMENT.MERCHANT_ID_OPTIONAL")}
        </label>
        <input
          type='text'
          value={credentials.razorpay.merchantId}
          onChange={(e) =>
            handleCredentialChange(
              "razorpay",
              "merchantId",
              e.target.value
            )
          }
          placeholder={t("PAYMENT.MERCHANT_ID_PLACEHOLDER")}
          className={`w-full px-4 py-3 border ${isDark ? "bg-slate-900 border-slate-700 text-white placeholder-slate-600 focus:ring-blue-500" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"} rounded-lg focus:ring-2 transition-colors duration-200`}
        />
      </div>
    </div>
  </>
)}

{selectedGateway === "cashfree" && (
  <>
    <div className='space-y-3'>
      <div>
        <label className={`block text-sm font-medium ${isDark ? "text-slate-300" : "text-gray-700"} mb-1`}>
          {t("PAYMENT.APP_ID")}
        </label>
        <input
          type='text'
          value={credentials.cashfree.appId}
          onChange={(e) =>
            handleCredentialChange(
              "cashfree",
              "appId",
              e.target.value
            )
          }
          placeholder={t("PAYMENT.APP_ID_PLACEHOLDER")}
          className={`w-full px-4 py-3 border ${isDark ? "bg-slate-900 border-slate-700 text-white placeholder-slate-600 focus:ring-blue-500" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"} rounded-lg focus:ring-2 transition-colors duration-200`}
        />
      </div>
      <div>
        <label className={`block text-sm font-medium ${isDark ? "text-slate-300" : "text-gray-700"} mb-1`}>
          {t("PAYMENT.SECRET_KEY")}
        </label>
        <input
          type='password'
          value={credentials.cashfree.secretKey}
          onChange={(e) =>
            handleCredentialChange(
              "cashfree",
              "secretKey",
              e.target.value
            )
          }
          placeholder={t("PAYMENT.SECRET_KEY_PLACEHOLDER")}
          className={`w-full px-4 py-3 border ${isDark ? "bg-slate-900 border-slate-700 text-white placeholder-slate-600 focus:ring-blue-500" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"} rounded-lg focus:ring-2 transition-colors duration-200`}
        />
      </div>
      <div className={`flex items-center space-x-3 ${isDark ? "bg-slate-900" : "bg-white"} p-3 rounded-lg border ${isDark ? "border-slate-700" : "border-gray-300"}`}>
        <input
          type='checkbox'
          checked={credentials.cashfree.isLive}
          onChange={(e) =>
            handleCredentialChange(
              "cashfree",
              "isLive",
              e.target.checked
            )
          }
          className={`h-4 w-4 text-blue-600 focus:ring-blue-500 rounded ${isDark ? "bg-slate-800 border-slate-600" : ""}`}
        />
        <label className={`text-sm font-medium ${isDark ? "text-slate-300" : "text-gray-700"}`}>
          {t("PAYMENT.LIVE_MODE")}
        </label>
      </div>
    </div>
  </>
)}
            {/* Actions */}
            <div className={`flex justify-end space-x-3 pt-4 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
              <button
                type='button'
                onClick={onClose}
                className={`px-6 py-3 border ${isDark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"} rounded-lg transition-colors duration-200 font-medium`}
              >
                {t("COMMON.CANCEL")}
              </button>
              <button
                type='button'
                onClick={handleVerify}
                disabled={isVerifying || !selectedGateway}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  isVerifying || !selectedGateway
                    ? (isDark ? "bg-slate-800 text-slate-600 cursor-not-allowed" : "bg-gray-300 text-gray-500 cursor-not-allowed")
                    : "bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                }`}
              >
                {isVerifying ? (
                  <span className='flex items-center space-x-2'>
                    {/* SVG icon remains same */}
                    <svg
                      className='w-4 h-4 animate-spin'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                      />
                    </svg>
                    <span>{t("PAYMENT.VERIFYING")}</span>
                  </span>
                ) : (
                  t("PAYMENT.VERIFY_AND_SAVE")
                )}
              </button>
            </div>
  
            {/* No Gateways Configured Section - Compact version */}
            {noGatewaysConfigured && (
              <div className={`bg-gradient-to-br ${isDark ? "from-blue-900/20 to-indigo-900/20 border-blue-900" : "from-blue-50 to-indigo-50 border-blue-200"} border rounded-xl p-4 animate-fade-in`}>
                <div className='text-center'>
                  <div className='flex items-center justify-center space-x-3 mb-3'>
                    {/* SVG icon remains same */}
                    <svg
                      className='w-5 h-5 text-blue-600'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1'
                      />
                    </svg>
                    <h4 className={`font-semibold ${isDark ? "text-slate-200" : "text-gray-800"} text-sm`}>
                      <p className={`${isDark ? "text-slate-400" : "text-gray-600"} mb-6 max-w-md mx-auto`}>
                        {t("PAYMENT.NO_GATEWAY_MESSAGE")}
                      </p>
                    </h4>
                  </div>

                  <div className='flex flex-col sm:flex-row gap-2 justify-center'>
                    <a
                      href='https://razorpay.com'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='inline-flex items-center justify-center px-3 py-2 bg-white border border-blue-300 rounded-lg text-sm text-blue-700 hover:bg-blue-50 transition-colors duration-200'
                    >
                      <span className='font-bold text-blue-600 mr-2'>RP</span>
                      {t("PAYMENT.CREATE_ACCOUNT_LINK", { gateway: t("PAYMENT.RAZORPAY") })}
                      <svg
                        className='w-3 h-3 ml-1'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                        />
                      </svg>
                    </a>

                    <a
                      href='https://www.cashfree.com'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='inline-flex items-center justify-center px-3 py-2 bg-white border border-orange-300 rounded-lg text-sm text-orange-700 hover:bg-orange-50 transition-colors duration-200'
                    >
                      <span className='font-bold text-orange-600 mr-2'>CF</span>
                      {t("PAYMENT.CREATE_ACCOUNT_LINK", { gateway: t("PAYMENT.CASHFREE") })}
                      <svg
                        className='w-3 h-3 ml-1'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                        />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentGatewayPopup;
